import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { ExpenseDialogProps } from '../types';

export function ExpenseDialog({
  isDark,
  isOpen,
  onOpenChange,
  payers,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
}: ExpenseDialogProps) {
  const inputCls = cn(
    isDark ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-slate-200',
  );
  const labelCls = cn(isDark ? 'text-slate-300' : 'text-slate-700');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-2xl', isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>{title}</DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelCls}>Дата *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Сумма *</Label>
              <Input
                type="number"
                min={0}
                value={formData.amount}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                className={inputCls}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Заголовок *</Label>
            <Input
              value={formData.title}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Напр. Хостинг DigitalOcean"
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Плательщик *</Label>
            <Select
              value={formData.payerId}
              onValueChange={(value) => onFormDataChange((prev) => ({ ...prev, payerId: value }))}
            >
              <SelectTrigger className={inputCls}>
                <SelectValue placeholder="Выберите плательщика" />
              </SelectTrigger>
              <SelectContent>
                {payers.map((payer) => (
                  <SelectItem key={payer.id} value={payer.id}>
                    {payer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {payers.length === 0 && (
              <p className="text-xs text-amber-500">
                Сначала добавьте плательщика (кнопка «Плательщики»).
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Описание</Label>
            <Input
              value={formData.description || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Необязательно"
              className={inputCls}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className={cn(isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200')}
          >
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
